import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Button,
  FormControlLabel,
  Checkbox,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tabs,
  Tab,
  Badge,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Image as ImageIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Notifications as NotificationsIcon,
  EventAvailable as EventAvailableIcon,
  Assignment as TaskIcon,
  FilterList as FilterListIcon,
  Person as PersonIcon,
  ClearAll as ClearAllIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  RateReview as RevisionIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import MentionInput from '../components/MentionInput';
import { processMentionsAndNotify } from '../services/mention.service';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useNotification } from '../contexts/NotificationContext';
import { format } from 'date-fns';

interface Borrower {
  id: string;
  name: string;
  mobile_no: string;
  address: string;
}

interface ItineraryItem {
  id: string;
  item_type?: 'borrower' | 'task';
  borrower_id?: string;
  borrower?: Borrower;
  task_title?: string;
  task_description?: string;
  visit_order: number;
  comments: string;
  collection_id?: string | null;
}

type ItineraryBorrower = ItineraryItem;

interface AgentProfile {
  id: string;
  username: string;
}

interface Itinerary {
  id: string;
  user_id: string;
  planned_date: Date;
  status: string;
  manager_comment?: string | null;
  borrowers: ItineraryBorrower[];
}

const PAGE_SIZE = 10;

export default function ApprovedItinerary() {
  const { userProfile } = useAuth();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Can this user view other agents' itineraries?
  const canViewAll =
    userProfile?.role === 'admin' ||
    userProfile?.role === 'manager' ||
    userProfile?.role === 'superagent';

  const todayDate = new Date();

  const [loading, setLoading] = useState(false);
  // Default to today so only today's itineraries load on first render
  const [startDate, setStartDate] = useState<Date | null>(todayDate);
  const [endDate, setEndDate] = useState<Date | null>(todayDate);
  const [agentFilter, setAgentFilter] = useState<string>('');
  const [agentsList, setAgentsList] = useState<AgentProfile[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [approvedPage, setApprovedPage] = useState(0);
  const [hasMoreApproved, setHasMoreApproved] = useState(false);

  // Pending review tab state (supervisors only)
  const [activeTab, setActiveTab] = useState<'approved' | 'pending'>('approved');
  const [pendingItineraries, setPendingItineraries] = useState<Itinerary[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingPage, setPendingPage] = useState(0);
  const [hasMorePending, setHasMorePending] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approved' | 'rejected' | 'revision_requested'>('approved');
  const [reviewTarget, setReviewTarget] = useState<Itinerary | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  const [editingComments, setEditingComments] = useState<Map<string, string>>(new Map());
  const [savingComments, setSavingComments] = useState<Set<string>>(new Set());
  const [selectedFiles, setSelectedFiles] = useState<Map<string, File>>(new Map());
  const [notifyFlags, setNotifyFlags] = useState<Map<string, boolean>>(new Map());
  const [previousUpdates, setPreviousUpdates] = useState<Map<string, any[]>>(new Map());
  const [loadingUpdates, setLoadingUpdates] = useState<Set<string>>(new Set());
  const [expandedUpdates, setExpandedUpdates] = useState<Set<string>>(new Set());
  const fileInputRefs = useRef<Map<string, HTMLInputElement | null>>(new Map());

  useEffect(() => {
    if (userProfile && canViewAll) {
      loadAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  useEffect(() => {
    if (userProfile) {
      // Reset to page 0 when any filter changes
      setApprovedPage(0);
      setPendingPage(0);
      if (activeTab === 'approved') {
        loadApprovedItineraries(0);
      } else if (canViewAll) {
        loadPendingItineraries(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, agentFilter, startDate, endDate, activeTab]);

  // Keep the pending badge count fresh
  useEffect(() => {
    if (userProfile && canViewAll) {
      supabase
        .from('itineraries')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'submitted')
        .is('deleted_at', null)
        .then(({ count }) => setPendingCount(count || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, pendingItineraries]);

  const loadAgents = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username')
        .eq('role', 'agent')
        .order('username');
      if (error) throw error;
      setAgentsList(data || []);
    } catch (error: any) {
      console.error('Error loading agents:', error);
    }
  };

  const clearFilters = () => {
    setStartDate(todayDate);
    setEndDate(todayDate);
    setAgentFilter('');
    setApprovedPage(0);
    setPendingPage(0);
  };

  const hasActiveFilters = !!startDate || !!endDate || !!agentFilter;

  const loadPendingItineraries = async (requestedPage = pendingPage) => {
    if (!userProfile || !canViewAll) return;
    setLoading(true);
    try {
      let query = supabase
        .from('itineraries')
        .select(`*, itinerary_borrowers(id, item_type, borrower_id, task_title, visit_order, comments, borrowers(id, name, mobile_no, address))`)
        .eq('status', 'submitted')
        .is('deleted_at', null)
        .order('planned_date', { ascending: false });

      if (agentFilter) query = query.eq('user_id', agentFilter);
      if (startDate) query = query.gte('planned_date', format(startDate, 'yyyy-MM-dd'));
      if (endDate) query = query.lte('planned_date', format(endDate, 'yyyy-MM-dd'));

      const from = requestedPage * PAGE_SIZE;
      const { data, error } = await query.range(from, from + PAGE_SIZE - 1);
      if (error) throw error;

      const rows = data || [];
      const transformed: Itinerary[] = rows.map((item: any) => ({
        id: item.id,
        user_id: item.user_id,
        planned_date: new Date(item.planned_date),
        status: item.status,
        manager_comment: item.manager_comment,
        borrowers: (item.itinerary_borrowers || [])
          .sort((a: any, b: any) => a.visit_order - b.visit_order)
          .map((ib: any) => ({
            id: ib.id,
            item_type: ib.item_type || 'borrower',
            borrower_id: ib.borrower_id,
            borrower: Array.isArray(ib.borrowers) ? ib.borrowers[0] : ib.borrowers,
            task_title: ib.task_title,
            visit_order: ib.visit_order,
            comments: ib.comments || '',
          })),
      }));

      if (requestedPage === 0) {
        setPendingItineraries(transformed);
      } else {
        setPendingItineraries(prev => [...prev, ...transformed]);
      }
      setPendingPage(requestedPage);
      setHasMorePending(rows.length === PAGE_SIZE);
    } catch (err: any) {
      console.error('Error loading pending itineraries:', err);
      showError('Failed to load pending itineraries');
    } finally {
      setLoading(false);
    }
  };

  const openReviewDialog = (
    itinerary: Itinerary,
    action: 'approved' | 'rejected' | 'revision_requested'
  ) => {
    setReviewTarget(itinerary);
    setReviewAction(action);
    setReviewComment('');
    setReviewDialogOpen(true);
  };

  const executeReview = async () => {
    if (!reviewTarget || !userProfile) return;
    setReviewLoading(true);
    try {
      const { error: updateError } = await supabase
        .from('itineraries')
        .update({
          status: reviewAction,
          manager_comment: reviewComment.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', reviewTarget.id);
      if (updateError) throw updateError;

      // Notify the agent
      const dateLabel = format(reviewTarget.planned_date, 'MMMM d, yyyy');
      const msgMap: Record<string, string> = {
        approved: `Your itinerary for ${dateLabel} has been approved! ✓`,
        rejected: `Your itinerary for ${dateLabel} was rejected.${reviewComment.trim() ? ` "${reviewComment.trim()}"` : ''}`,
        revision_requested: `Your itinerary for ${dateLabel} needs revision.${reviewComment.trim() ? ` "${reviewComment.trim()}"` : ''}`,
      };
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);
      await supabase.from('notifications').insert({
        recipient_id: reviewTarget.user_id,
        message: msgMap[reviewAction],
        category: 'collection',
        notification_type: 'collection_case',
        is_read: false,
        is_active: true,
        reference_id: reviewTarget.id,
        expiry_date: expiryDate.toISOString().split('T')[0],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });

      const labels: Record<string, string> = {
        approved: 'Approved!',
        rejected: 'Rejected.',
        revision_requested: 'Revision requested.',
      };
      showSuccess(labels[reviewAction]);
      setReviewDialogOpen(false);
      await loadPendingItineraries(0);
      setPendingPage(0);
      if (reviewAction === 'approved') { loadApprovedItineraries(0); setApprovedPage(0); }
    } catch (err: any) {
      console.error('Review error:', err);
      showError('Failed to update itinerary status');
    } finally {
      setReviewLoading(false);
    }
  };

  const loadApprovedItineraries = async (requestedPage = approvedPage) => {
    if (!userProfile) return;
    setLoading(true);
    try {
      let query = supabase
        .from('itineraries')
        .select(`
          *,
          itinerary_borrowers (
            id, item_type, borrower_id, task_title, task_description, visit_order, comments,
            borrowers ( id, name, mobile_no, address )
          )
        `)
        .eq('status', 'approved')
        .is('deleted_at', null)
        .order('planned_date', { ascending: false });

      if (!canViewAll) {
        query = query.eq('user_id', userProfile.id);
      } else if (agentFilter) {
        query = query.eq('user_id', agentFilter);
      }

      if (startDate) query = query.gte('planned_date', format(startDate, 'yyyy-MM-dd'));
      if (endDate) query = query.lte('planned_date', format(endDate, 'yyyy-MM-dd'));

      const from = requestedPage * PAGE_SIZE;
      const { data, error } = await query.range(from, from + PAGE_SIZE - 1);
      if (error) throw error;

      const rows = data || [];

      // ── Batch-fetch collection IDs to eliminate the N+1 query ───────────
      const borrowerIds = Array.from(
        new Set(
          rows
            .flatMap((it: any) => it.itinerary_borrowers || [])
            .filter((ib: any) => ib.item_type !== 'task' && ib.borrower_id)
            .map((ib: any) => ib.borrower_id as string)
        )
      );
      const collectionMap: Record<string, string> = {};
      if (borrowerIds.length > 0) {
        const { data: colData } = await supabase
          .from('for_collection')
          .select('id, borrower_id')
          .in('borrower_id', borrowerIds)
          .is('deleted_at', null)
          .limit(500);
        (colData || []).forEach((c: any) => {
          collectionMap[c.borrower_id] = c.id;
        });
      }
      // ────────────────────────────────────────────────────────────────────

      const transformedData: Itinerary[] = rows.map((item: any) => ({
        id: item.id,
        user_id: item.user_id,
        planned_date: new Date(item.planned_date),
        status: item.status,
        manager_comment: item.manager_comment,
        borrowers: (item.itinerary_borrowers || [])
          .map((ib: any) => {
            const itemType: 'borrower' | 'task' = ib.item_type || 'borrower';
            const borrower = Array.isArray(ib.borrowers) ? ib.borrowers[0] : ib.borrowers;
            return {
              id: ib.id,
              item_type: itemType,
              borrower_id: ib.borrower_id,
              borrower: itemType !== 'task' ? borrower : undefined,
              task_title: ib.task_title,
              task_description: ib.task_description,
              visit_order: ib.visit_order,
              comments: ib.comments || '',
              collection_id:
                itemType !== 'task' && ib.borrower_id
                  ? (collectionMap[ib.borrower_id] ?? null)
                  : null,
            };
          })
          .sort((a: any, b: any) => a.visit_order - b.visit_order),
      }));

      if (requestedPage === 0) {
        setItineraries(transformedData);
      } else {
        setItineraries(prev => [...prev, ...transformedData]);
      }
      setApprovedPage(requestedPage);
      setHasMoreApproved(rows.length === PAGE_SIZE);
    } catch (error: any) {
      console.error('Error loading approved itineraries:', error);
      showError('Failed to load approved itineraries');
    } finally {
      setLoading(false);
    }
  };

  const startEditingComment = (id: string) => {
    const newMap = new Map(editingComments);
    newMap.set(id, '');
    setEditingComments(newMap);
  };

  const cancelEditingComment = (id: string) => {
    const newMap = new Map(editingComments);
    newMap.delete(id);
    setEditingComments(newMap);
  };

  const updateEditingComment = (id: string, value: string) => {
    const newMap = new Map(editingComments);
    newMap.set(id, value);
    setEditingComments(newMap);
  };

  const loadPreviousUpdates = async (itineraryBorrowerId: string, collectionId: string) => {
    setLoadingUpdates(prev => new Set(prev).add(itineraryBorrowerId));
    try {
      const { data, error } = await supabase
        .from('borrower_updates')
        .select('id, content, image_url, created_at, notify, agent_name')
        .eq('collection_id', collectionId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      setPreviousUpdates(prev => new Map(prev).set(itineraryBorrowerId, data || []));
    } catch (error: any) {
      console.error('Error loading previous updates:', error);
    } finally {
      setLoadingUpdates(prev => {
        const next = new Set(prev);
        next.delete(itineraryBorrowerId);
        return next;
      });
    }
  };

  const savePostCollectionComment = async (
    itineraryBorrowerId: string,
    collectionId: string | null,
    borrowerId: string,
    borrowerName: string
  ) => {
    const comment = editingComments.get(itineraryBorrowerId);
    if (!comment || comment.trim() === '' || !userProfile) return;

    if (!collectionId) {
      showError('Cannot post: this borrower is not yet in the collection system.');
      return;
    }

    setSavingComments(prev => new Set(prev).add(itineraryBorrowerId));
    try {
      let imageUrl = '';
      const selectedFile = selectedFiles.get(itineraryBorrowerId);
      if (selectedFile) {
        const fileExt = selectedFile.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('updates')
          .upload(fileName, selectedFile, { cacheControl: '3600', upsert: false });

        if (uploadError) throw new Error('Failed to upload image');

        const { data: { publicUrl } } = supabase.storage.from('updates').getPublicUrl(fileName);
        imageUrl = publicUrl;
      }

      const { data: updateData, error } = await supabase
        .from('borrower_updates')
        .insert({
          collection_id: collectionId,
          agent_id: userProfile.id,
          content: comment.trim(),
          image_url: imageUrl || null,
          notify: notifyFlags.get(itineraryBorrowerId) || false,
          agent_name: userProfile.username || 'Unknown Agent',
        })
        .select('id')
        .single();

      if (error) throw error;

      if (updateData) {
        await processMentionsAndNotify(
          comment.trim(),
          userProfile.id,
          userProfile.username || 'Unknown Agent',
          updateData.id,
          false,
          undefined,
          borrowerName
        );
      }

      showSuccess('Posted to borrower activity');
      cancelEditingComment(itineraryBorrowerId);

      const newFiles = new Map(selectedFiles);
      newFiles.delete(itineraryBorrowerId);
      setSelectedFiles(newFiles);

      const newFlags = new Map(notifyFlags);
      newFlags.delete(itineraryBorrowerId);
      setNotifyFlags(newFlags);

      await loadPreviousUpdates(itineraryBorrowerId, collectionId);
    } catch (error: any) {
      console.error('Error saving post:', error);
      showError('Failed to post to borrower activity');
    } finally {
      setSavingComments(prev => {
        const next = new Set(prev);
        next.delete(itineraryBorrowerId);
        return next;
      });
    }
  };

  const toggleExpand = (itemId: string, collectionId?: string | null) => {
    setExpandedUpdates(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
        if (collectionId && !previousUpdates.has(itemId)) {
          loadPreviousUpdates(itemId, collectionId);
        }
      }
      return next;
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="md" sx={{ py: { xs: 1.5, sm: 4 }, px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 0, sm: 3 },
            border: { xs: 'none', sm: '1px solid' },
            borderColor: 'divider',
            borderRadius: { xs: 0, sm: 2 },
            bgcolor: { xs: 'transparent', sm: 'background.paper' },
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 2, sm: 3 } }}>
            <EventAvailableIcon color="success" sx={{ fontSize: { xs: 28, sm: 32 } }} />
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
              Approved Itineraries
            </Typography>
          </Box>

          {/* Tab bar — only for supervisors */}
          {canViewAll && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ minHeight: 40 }}
              >
                <Tab
                  label="Approved"
                  value="approved"
                  icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                  iconPosition="start"
                  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                />
                <Tab
                  label={
                    <Badge badgeContent={pendingCount} color="warning" max={99}>
                      <Box sx={{ pr: pendingCount > 0 ? 1.5 : 0 }}>Pending Review</Box>
                    </Badge>
                  }
                  value="pending"
                  icon={<PendingIcon sx={{ fontSize: 16 }} />}
                  iconPosition="start"
                  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                />
              </Tabs>
            </Box>
          )}

          {/* Filter Bar */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: showFilters ? 1.5 : 0,
              }}
            >
              <Button
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(v => !v)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                color={hasActiveFilters ? 'primary' : 'inherit'}
              >
                {hasActiveFilters
                  ? `Filters (${[!!startDate, !!endDate, !!agentFilter].filter(Boolean).length} active)`
                  : 'Filters'}
                {showFilters ? (
                  <ExpandLessIcon fontSize="small" sx={{ ml: 0.5 }} />
                ) : (
                  <ExpandMoreIcon fontSize="small" sx={{ ml: 0.5 }} />
                )}
              </Button>
              {hasActiveFilters && (
                <Button
                  size="small"
                  startIcon={<ClearAllIcon />}
                  onClick={clearFilters}
                  color="error"
                  variant="text"
                >
                  Clear
                </Button>
              )}
            </Box>

            {showFilters && (
              <Paper
                variant="outlined"
                sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, bgcolor: 'grey.50' }}
              >
                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="From Date"
                      value={startDate}
                      onChange={(v) => setStartDate(v)}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small' },
                        actionBar: { actions: ['clear', 'today'] },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="To Date"
                      value={endDate}
                      onChange={(v) => setEndDate(v)}
                      minDate={startDate || undefined}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small' },
                        actionBar: { actions: ['clear', 'today'] },
                      }}
                    />
                  </Grid>
                  {canViewAll && (
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Agent</InputLabel>
                        <Select
                          value={agentFilter}
                          label="Agent"
                          onChange={(e) => setAgentFilter(e.target.value)}
                          startAdornment={<PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />}
                        >
                          <MenuItem value="">All Agents</MenuItem>
                          {agentsList.map((agent) => (
                            <MenuItem key={agent.id} value={agent.id}>
                              {agent.username}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : activeTab === 'pending' && canViewAll ? (
            /* ─── Pending Review list ─── */
            pendingItineraries.length === 0 ? (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                All clear — no itineraries pending review.
                {hasActiveFilters && ' Try adjusting or clearing the filters.'}
              </Alert>
            ) : (
              <Box>
                {pendingItineraries.map((itinerary) => {
                  const agentName = agentsList.find(a => a.id === itinerary.user_id)?.username;
                  return (
                    <Card
                      key={itinerary.id}
                      sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main', borderRadius: 2 }}
                    >
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="bold">
                              {format(itinerary.planned_date, 'EEEE, MMMM dd, yyyy')}
                            </Typography>
                            {agentName && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem', bgcolor: 'primary.main' }}>
                                  {agentName.charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                  {agentName}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Chip label="PENDING REVIEW" color="warning" size="small" icon={<PendingIcon />} />
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {itinerary.borrowers.length} item{itinerary.borrowers.length !== 1 ? 's' : ''} planned
                          {itinerary.borrowers.filter(b => b.item_type === 'task').length > 0
                            ? ` (${itinerary.borrowers.filter(b => b.item_type !== 'task').length} visits, ${itinerary.borrowers.filter(b => b.item_type === 'task').length} tasks)`
                            : ''}
                        </Typography>

                        <Divider sx={{ my: 1.25 }} />

                        {/* Borrower/task summary */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                          {itinerary.borrowers.map((item) => (
                            <Chip
                              key={item.id}
                              label={item.item_type === 'task' ? (item.task_title || 'Task') : (item.borrower?.name || 'Borrower')}
                              size="small"
                              variant="outlined"
                              color={item.item_type === 'task' ? 'warning' : 'default'}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>

                        {/* Action buttons */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<RevisionIcon />}
                            fullWidth={isMobile}
                            onClick={() => openReviewDialog(itinerary, 'revision_requested')}
                            sx={{ textTransform: 'none', borderRadius: 1.5 }}
                          >
                            Request Revision
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            fullWidth={isMobile}
                            onClick={() => openReviewDialog(itinerary, 'rejected')}
                            sx={{ textTransform: 'none', borderRadius: 1.5 }}
                          >
                            Reject
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            fullWidth={isMobile}
                            onClick={() => openReviewDialog(itinerary, 'approved')}
                            sx={{ textTransform: 'none', borderRadius: 1.5 }}
                          >
                            Approve
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}

                {/* Load More — pending */}
                {hasMorePending && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                    <Button
                      variant="outlined"
                      onClick={() => loadPendingItineraries(pendingPage + 1)}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={16} /> : undefined}
                    >
                      Load More
                    </Button>
                  </Box>
                )}
              </Box>
            )
          ) : itineraries.length === 0 ? (
            <Alert severity="info">
              No approved itineraries found for the selected date range.
              Use the Filters to broaden the date range or clear to see all.
            </Alert>
          ) : (
            <Box>
              {itineraries.map((itinerary) => (
                <Card
                  key={itinerary.id}
                  sx={{ mb: 3, border: '1px solid', borderColor: 'success.main', borderRadius: 2 }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                    {/* Itinerary Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold">
                          {format(itinerary.planned_date, 'EEEE, MMMM dd, yyyy')}
                        </Typography>
                        {canViewAll && (() => {
                          const agentName = agentsList.find(a => a.id === itinerary.user_id)?.username;
                          return agentName ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                              <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem', bgcolor: 'primary.main' }}>
                                {agentName.charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                {agentName}
                              </Typography>
                            </Box>
                          ) : null;
                        })()}
                      </Box>
                      <Chip label="APPROVED" color="success" size="small" icon={<CheckIcon />} />
                    </Box>

                    {itinerary.manager_comment && (
                      <Alert severity="info" sx={{ mt: 1, mb: 1.5 }}>
                        <Typography variant="caption" fontWeight="bold">Manager Comment: </Typography>
                        <Typography variant="body2">{itinerary.manager_comment}</Typography>
                      </Alert>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1.5 }}>
                      ITINERARY ITEMS ({itinerary.borrowers.length})
                    </Typography>

                    <List disablePadding>
                      {itinerary.borrowers.map((item) => {
                        const isEditing = editingComments.has(item.id);
                        const isSaving = savingComments.has(item.id);
                        const isTask = item.item_type === 'task';

                        return (
                          <Card
                            key={item.id}
                            sx={{
                              mb: 1.5,
                              bgcolor: 'background.default',
                              border: '1px solid',
                              borderColor: isTask ? 'warning.light' : 'divider',
                              borderRadius: 2,
                            }}
                          >
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                {/* Order badge */}
                                <Box
                                  sx={{
                                    minWidth: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: isTask ? 'warning.main' : 'success.main',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem',
                                    flexShrink: 0,
                                  }}
                                >
                                  {item.visit_order}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  {isTask ? (
                                    <>
                                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                        <TaskIcon fontSize="small" color="warning" />
                                        <Typography variant="subtitle1" fontWeight="bold">
                                          {item.task_title}
                                        </Typography>
                                        <Chip label="Task" size="small" color="warning" />
                                      </Box>
                                      {item.task_description && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                          {item.task_description}
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                          {item.collection_id ? (
                                            <Link
                                              to={`/borrower/${item.collection_id}`}
                                              style={{ textDecoration: 'none', color: 'inherit' }}
                                            >
                                              {item.borrower?.name}
                                            </Link>
                                          ) : (
                                            item.borrower?.name
                                          )}
                                        </Typography>
                                        <Chip label="Borrower" size="small" color="primary" />
                                      </Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        {item.borrower?.mobile_no} · {item.borrower?.address}
                                      </Typography>
                                    </>
                                  )}

                                  {/* Pre-collection notes */}
                                  {item.comments && (
                                    <Box
                                      sx={{
                                        mt: 0.5,
                                        p: 1.5,
                                        bgcolor: 'info.50',
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'info.light',
                                        mb: 1,
                                      }}
                                    >
                                      <Typography variant="caption" fontWeight="bold" color="info.dark">
                                        {isTask ? 'Notes:' : 'Pre-Visit Notes:'}
                                      </Typography>
                                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                                        {item.comments}
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Post-collection activity — borrowers only */}
                                  {!isTask && (
                                    <Box sx={{ mt: 1.5 }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1,
                                        }}
                                      >
                                        <Typography variant="caption" fontWeight="bold" color="success.dark">
                                          Field Activity
                                        </Typography>

                                        {!isEditing && item.collection_id && (
                                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title={expandedUpdates.has(item.id) ? 'Hide posts' : 'View posts'}>
                                              <IconButton
                                                size="small"
                                                color="info"
                                                onClick={() => toggleExpand(item.id, item.collection_id)}
                                              >
                                                {expandedUpdates.has(item.id)
                                                  ? <ExpandLessIcon fontSize="small" />
                                                  : <ExpandMoreIcon fontSize="small" />}
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Refresh">
                                              <IconButton
                                                size="small"
                                                onClick={() => loadPreviousUpdates(item.id, item.collection_id!)}
                                                disabled={loadingUpdates.has(item.id)}
                                              >
                                                <RefreshIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Add post">
                                              <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => startEditingComment(item.id)}
                                              >
                                                <EditIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        )}
                                      </Box>

                                      {!item.collection_id && (
                                        <Alert severity="warning" sx={{ mb: 1 }}>
                                          <Typography variant="caption">
                                            Borrower not in collection system — posts unavailable.
                                          </Typography>
                                        </Alert>
                                      )}

                                      {/* Previous posts */}
                                      {item.collection_id && expandedUpdates.has(item.id) && (
                                        <Box sx={{ mb: 1.5 }}>
                                          {loadingUpdates.has(item.id) ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                              <CircularProgress size={20} />
                                            </Box>
                                          ) : (previousUpdates.get(item.id) || []).length === 0 ? (
                                            <Alert severity="info">
                                              <Typography variant="caption">No posts yet.</Typography>
                                            </Alert>
                                          ) : (
                                            <Stack spacing={1}>
                                              {(previousUpdates.get(item.id) || []).map((update: any) => (
                                                <Card key={update.id} sx={{ bgcolor: 'grey.50' }}>
                                                  <CardContent
                                                    sx={{
                                                      py: 1.5,
                                                      px: { xs: 1.5, sm: 2 },
                                                      '&:last-child': { pb: 1.5 },
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        mb: 0.5,
                                                        flexWrap: 'wrap',
                                                        gap: 0.5,
                                                      }}
                                                    >
                                                      <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                                        {update.agent_name}
                                                      </Typography>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {update.notify && (
                                                          <Tooltip title="Marked important">
                                                            <NotificationsIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                                          </Tooltip>
                                                        )}
                                                        <Typography variant="caption" color="text.secondary">
                                                          {format(new Date(update.created_at), isMobile ? 'MMM d, yy h:mm a' : 'MMM dd, yyyy h:mm a')}
                                                        </Typography>
                                                      </Box>
                                                    </Box>
                                                    <Typography variant="body2">{update.content}</Typography>
                                                    {update.image_url && (
                                                      <Box sx={{ mt: 1 }}>
                                                        <img
                                                          src={update.image_url}
                                                          alt="Update"
                                                          style={{
                                                            maxWidth: '100%',
                                                            maxHeight: 200,
                                                            borderRadius: 4,
                                                            objectFit: 'contain',
                                                          }}
                                                        />
                                                      </Box>
                                                    )}
                                                  </CardContent>
                                                </Card>
                                              ))}
                                            </Stack>
                                          )}
                                        </Box>
                                      )}

                                      {/* New Post Form */}
                                      {isEditing && (
                                        <Box
                                          sx={{
                                            p: { xs: 1.5, sm: 2 },
                                            bgcolor: 'background.paper',
                                            borderRadius: 1.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                          }}
                                        >
                                          <MentionInput
                                            value={editingComments.get(item.id) || ''}
                                            onChange={(value) => updateEditingComment(item.id, value)}
                                            placeholder="Add a field update… (use @ to mention people)"
                                            disabled={isSaving}
                                            multiline
                                            rows={3}
                                          />

                                          {/* File & options row */}
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              gap: 1,
                                              alignItems: 'center',
                                              mt: 1.5,
                                              flexWrap: 'wrap',
                                            }}
                                          >
                                            <input
                                              ref={(el) => fileInputRefs.current.set(item.id, el)}
                                              type="file"
                                              accept="image/*"
                                              style={{ display: 'none' }}
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file && file.type.startsWith('image/')) {
                                                  setSelectedFiles(prev => new Map(prev).set(item.id, file));
                                                }
                                              }}
                                            />
                                            <Button
                                              size="small"
                                              startIcon={<ImageIcon />}
                                              onClick={() => fileInputRefs.current.get(item.id)?.click()}
                                              disabled={isSaving}
                                              variant="outlined"
                                            >
                                              {selectedFiles.has(item.id) ? 'Change Photo' : 'Add Photo'}
                                            </Button>

                                            {selectedFiles.has(item.id) && (
                                              <Typography variant="caption" color="text.secondary" noWrap>
                                                {selectedFiles.get(item.id)?.name}
                                              </Typography>
                                            )}

                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={notifyFlags.get(item.id) || false}
                                                  onChange={(e) =>
                                                    setNotifyFlags(prev => new Map(prev).set(item.id, e.target.checked))
                                                  }
                                                  size="small"
                                                />
                                              }
                                              label={
                                                <Typography variant="caption">Mark Important</Typography>
                                              }
                                            />
                                          </Box>

                                          <Box
                                            sx={{
                                              display: 'flex',
                                              gap: 1,
                                              mt: 1.5,
                                              flexDirection: { xs: 'column', sm: 'row' },
                                            }}
                                          >
                                            <Button
                                              variant="contained"
                                              color="success"
                                              startIcon={<SaveIcon />}
                                              onClick={() =>
                                                savePostCollectionComment(
                                                  item.id,
                                                  item.collection_id || null,
                                                  item.borrower_id || '',
                                                  item.borrower?.name || 'Unknown'
                                                )
                                              }
                                              disabled={isSaving || !editingComments.get(item.id)?.trim()}
                                              fullWidth={isMobile}
                                              size={isMobile ? 'large' : 'medium'}
                                            >
                                              {isSaving ? 'Posting…' : 'Post Activity'}
                                            </Button>
                                            <Button
                                              variant="outlined"
                                              startIcon={<CancelIcon />}
                                              onClick={() => {
                                                cancelEditingComment(item.id);
                                                setSelectedFiles(prev => {
                                                  const next = new Map(prev);
                                                  next.delete(item.id);
                                                  return next;
                                                });
                                                setNotifyFlags(prev => {
                                                  const next = new Map(prev);
                                                  next.delete(item.id);
                                                  return next;
                                                });
                                              }}
                                              disabled={isSaving}
                                              fullWidth={isMobile}
                                              size={isMobile ? 'large' : 'medium'}
                                            >
                                              Cancel
                                            </Button>
                                          </Box>
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </List>
                  </CardContent>
                </Card>
              ))}

              {/* Load More — approved */}
              {hasMoreApproved && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => loadApprovedItineraries(approvedPage + 1)}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : undefined}
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        {/* ─── Review Dialog (Approve / Reject / Request Revision) ─── */}
        <Dialog
          open={reviewDialogOpen}
          onClose={() => !reviewLoading && setReviewDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {reviewAction === 'approved' && <CheckCircleIcon color="success" />}
              {reviewAction === 'rejected' && <CancelIcon color="error" />}
              {reviewAction === 'revision_requested' && <RevisionIcon color="warning" />}
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {reviewAction === 'approved' ? 'Approve Itinerary'
                  : reviewAction === 'rejected' ? 'Reject Itinerary'
                  : 'Request Revision'}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setReviewDialogOpen(false)} disabled={reviewLoading}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            {reviewTarget && (
              <Alert
                severity={reviewAction === 'approved' ? 'success' : reviewAction === 'rejected' ? 'error' : 'warning'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  {format(reviewTarget.planned_date, 'EEEE, MMMM dd, yyyy')}
                  {agentsList.find(a => a.id === reviewTarget.user_id)
                    ? ` — ${agentsList.find(a => a.id === reviewTarget.user_id)?.username}`
                    : ''}
                </Typography>
              </Alert>
            )}
            <TextField
              label={reviewAction === 'approved' ? 'Optional comment for the agent' : 'Comment (required for rejection/revision)'}
              multiline
              minRows={3}
              fullWidth
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder={
                reviewAction === 'approved'
                  ? 'Great plan! See you in the field…'
                  : reviewAction === 'rejected'
                  ? 'Please resubmit with corrections…'
                  : 'Please add the following stops…'
              }
              size={isMobile ? 'small' : 'medium'}
            />
          </DialogContent>

          <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
            <Button
              variant="outlined"
              fullWidth={isMobile}
              onClick={() => setReviewDialogOpen(false)}
              disabled={reviewLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth={isMobile}
              color={reviewAction === 'approved' ? 'success' : reviewAction === 'rejected' ? 'error' : 'warning'}
              disabled={reviewLoading || (reviewAction !== 'approved' && reviewComment.trim() === '')}
              onClick={executeReview}
              startIcon={
                reviewLoading ? <CircularProgress size={16} color="inherit" /> :
                reviewAction === 'approved' ? <CheckCircleIcon /> :
                reviewAction === 'rejected' ? <CancelIcon /> :
                <RevisionIcon />
              }
            >
              {reviewLoading ? 'Saving…'
                : reviewAction === 'approved' ? 'Approve & Notify Agent'
                : reviewAction === 'rejected' ? 'Reject & Notify Agent'
                : 'Request Revision & Notify Agent'}
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </LocalizationProvider>
  );
}
